import update from 'immutability-helper';
import { handleActions } from 'redux-actions';
import {
  property,
  find,
  cloneDeep,
  findLastIndex,
  uniq,
  isEmpty,
  isArray,
  get,
  without,
  flatten,
  last,
  groupBy,
  noop,
} from 'lodash';
import { createSelector } from 'reselect';
import { getFormValues, hasSubmitFailed, isInvalid } from 'redux-form';
import { LOCATION_CHANGE } from 'connected-react-router';

import {
  createEmptyOptionCollection,
  questionTypes,
  SURVEY_SECTION_END,
  SURVEY_SECTION_LANDING,
  SURVEY_SECTION_QUESTIONS,
  SURVEY_STATUS_CLOSED,
  SURVEY_STATUS_DRAFT,
  SURVEY_STATUS_PAUSED,
} from 'sharedParts';
import blockConfig from 'sharedParts/config/blockConfig';
import segmentLabelMap from 'sharedParts/config/segmentLabelMap';
import isPageBreak from 'utils/isPageBreak';
import Constants from 'constants/actions';
import mapEditorSectionToUrl from 'config/mapEditorSectionToUrl';
import { respondentBlockMorphList } from 'config/blockEditTools/surveyBlockMorphList';
import isPostLogicAvailableBlockType from 'utils/isPostLogicAvailableBlockType';
import getReducerInitialState from 'utils/getReducerInitialState';
import { addPageNumbersToSections } from 'utils/addPageNumberToBlocks';
import questionLogicUtils from 'utils/questionLogicUtils';

import postSurveyLogic, * as fromPostSurvey from './postSurveyLogicReducer';
import sectionHeadersReducer, * as fromHeader from './sectionHeadersReducer';
import getUrl from '../utils/getUrl';
import { properSectionWithNextButtonLength } from '../config/sectionsConfig';
import surveyBuilderSections from '../config/surveyBuilderSections';
import validateRedirectUrl from '../utils/validateRedirectUrl';
import { SURVEY_TYPE, TEMPLATE_TYPE } from '../config/EntityTypeList';
import { surveyEditorRoot } from '../routes/paths';
import entityCollectionPathMap from '../api/config/EntityCollectionPathMap';
import routerRootPathEntityMap from '../config/RouterRootPathEntityMap';
import DefaultSurveySettings from '../config/DefaultSurveySettings';
import stickyButtonsConfig from '../config/stickyButtonsConfig';
import { isQuestionsSection } from '../config/surveySectionsList';
import { REPORT_FREQUENCY_NONE } from '../constants/reportFrequency';
import { addQuestionNumbers } from '../utils/questionNumberingUtils';

const { itemList } = segmentLabelMap;
const { landing, survey, ty } = mapEditorSectionToUrl;

const initialState = {
  activeEntityType: SURVEY_TYPE,
  newNotPersistedBlockUuidList: [],
  modifiedNewNotPersistedBlockUuidList: [],
  activeSectionName: null,
  surveyData: {
    landing: [],
    questions: [],
    end: [],
    header: [],
    landingEnabled: true,
    endEnabled: true,
    redirectUrl: '',
    id: null,
    uuid: null,
    status: SURVEY_STATUS_DRAFT,
    name: '',
    slug: '',
    dateLaunched: null,
    isFullWidth: false,
    settings: DefaultSurveySettings,
    isStatusPending: false,
    headerUuid: null,
  },
  cachedBlockDataMap: {},
  postSurveyLogic: getReducerInitialState(postSurveyLogic),
  slug: '',
  orderMap: {
    landing: {},
    questions: {},
    end: {},
  },
  isAddBlockNetworkPending: false,
  isMoveBlockNetworkPending: false,
  isSurveyNameFocused: false,
  isSetOverwriteCustomStylesEnabled: false,
  pendingBlockList: {
    landing: [],
    questions: [],
    end: [],
  },
  touchedBlockUuidList: [],
  pendingOrderMap: null,
  activeBlock: { clientUuid: null, isLogicPanelOpened: false },
  latestAddedBlockClientUuid: null,
  latestRemovedBlockClientUuid: null,
  isLoading: true,
  inlineStylesToCopy: null,
  sectionHeaders: {},
};

const empty = {
  array: [],
  object: {},
};

// @todo move reducers to redux actions handling @feshchenko
const switchHandle = (state = initialState, action) => {
  switch (action.type) {
    case Constants.SET_POST_SURVEY_LOGIC:
    case Constants.SET_POST_LOGIC_QUESTION_ID:
    case Constants.ADD_POST_LOGIC_RULE:
    case Constants.SET_POST_LOGIC_RULE:
    case Constants.REMOVE_POST_LOGIC_RULE:
    case Constants.RESET_POST_LOGIC_RULE:
    case Constants.REMOVE_POST_LOGIC_ITEM:
    case Constants.TOGGLE_POST_LOGIC_ITEM:
    case Constants.ADD_POST_LOGIC_ITEM: {
      return update(state, {
        postSurveyLogic: {
          $set: postSurveyLogic(state.postSurveyLogic, action),
        },
      });
    }
    case Constants.CLEAR_SECTION_HEADER:
    case Constants.CLEAR_SECTION_HEADERS:
    case Constants.SET_SECTION_HEADER:
    case Constants.SET_SECTION_HEADERS:
    case Constants.UPDATE_SECTION_HEADER:
    case Constants.UPDATE_SECTION_HEADERS: {
      return update(state, {
        sectionHeaders: {
          $set: sectionHeadersReducer(state.sectionHeaders, action),
        },
      });
    }

    default: {
      return state;
    }
  }
};

export const getActiveEntityType = (state) => state.activeEntityType;

export const getNewNotPersistedBlockUuidList = (state) => state.newNotPersistedBlockUuidList;

export const getModifiedNewNotPersistedBlockUuidList = (state) => state.modifiedNewNotPersistedBlockUuidList;

export const wasModifiedButNotPersisted = (state, clientUuid) =>
  getModifiedNewNotPersistedBlockUuidList(state).includes(clientUuid);

export const getRawSurveyData = (state) => get(state, 'surveyData', {});

export const getActiveEntityId = (state) => get(getRawSurveyData(state), 'id');

export const getActiveEntityCollectionPath = (state) => entityCollectionPathMap[getActiveEntityType(state)];

export const getCurrentRouterRootPath = (state) => routerRootPathEntityMap[getActiveEntityType(state)].item;

export const getCurrentRouterListRootPath = (state) =>
  routerRootPathEntityMap[getActiveEntityType(state)].list;

export const getRawSectionBlockList = (sectionName) =>
  createSelector(getRawSurveyData, (rawSurveyData) => rawSurveyData[sectionName]);

export const getPaginatedSurveyData = createSelector(getRawSurveyData, (surveyData) => {
  const { landing, questions, end } = surveyData;

  return {
    ...surveyData,
    ...addPageNumbersToSections({ landing, questions, end }),
  };
});

export const getCachedBlockDataMap = (state) => state.cachedBlockDataMap;

export const getActiveSection = (state) => get(state, 'activeSectionName');

export const isSectionActive = (state, sectionName) => getActiveSection(state) === sectionName;

export const getSectionBlocks = (state, sectionName) => {
  const data = getPaginatedSurveyData(state)[sectionName];
  return isArray(data) ? data : empty.array;
};

export const getSectionBlockTypes = createSelector(getSectionBlocks, (blocks) =>
  blocks.map(property('type')),
);

export const getActiveSectionBlocks = (state) => getSectionBlocks(state, getActiveSection(state));

const getSurveyEditorSection = (state, section) => get(getPaginatedSurveyData(state), section, empty.array);

export const isLandingEnabled = (state) => getRawSurveyData(state).landingEnabled;

export const isThankYouEnabled = (state) => getRawSurveyData(state).endEnabled;

export const getQuestions = (state) => getSurveyEditorSection(state, 'questions');
export const getLanding = (state) => getSurveyEditorSection(state, 'landing');
export const getEnd = (state) => getSurveyEditorSection(state, 'end');

export const getAllBlocks = createSelector(
  getLanding,
  getQuestions,
  getEnd,
  (blocksLanding, blocksQuestion, blocksEnd) => [...blocksLanding, ...blocksQuestion, ...blocksEnd],
);

export const getEnabledSectionsBlocks = createSelector(
  isLandingEnabled,
  isThankYouEnabled,
  getLanding,
  getQuestions,
  getEnd,
  (includeLanding, includeEnd, blocksLanding, blocksQuestion, blocksEnd) => [
    ...(includeLanding ? blocksLanding : empty.array),
    ...blocksQuestion,
    ...(includeEnd ? blocksEnd : empty.array),
  ],
);

export const getEnabledSectionsPageToQuestionListMap = createSelector(getEnabledSectionsBlocks, (blocks) =>
  groupBy(flatten(blocks), 'page'),
);

export const getEntireSurveyFlattenedBlockList = createSelector(getAllBlocks, (blocks) => flatten(blocks));

export const isQuestionsSectionOnePage = createSelector(
  getEnabledSectionsPageToQuestionListMap,
  (pageToBlocksListMap) => {
    const flatQuestionPageList = Object.entries(pageToBlocksListMap).reduce((accumulator, item) => {
      const pageBlockList = last(item);
      const isPageInQuestionSection = pageBlockList.some(({ section }) => isQuestionsSection(section));
      if (isPageInQuestionSection) accumulator.push(item);
      return accumulator;
    }, []);
    return flatQuestionPageList.length === 1;
  },
);

export const getMaxPageNumber = createSelector(getEntireSurveyFlattenedBlockList, (blocks) =>
  Math.max(...blocks.map(property('page'))),
);

export const getEndPageNumber = createSelector(
  getEnd,
  getMaxPageNumber,
  (blocksEnd, maxPageNumber) => (isEmpty(blocksEnd) ? maxPageNumber + 1 : maxPageNumber),
);

export const getPageToStartPreview = createSelector(
  getActiveSection,
  getActiveSectionBlocks,
  getEndPageNumber,
  (activeSection, activeSectionBlocks, endPageNumber) => {
    switch (activeSection) {
      // eslint-disable-next-line no-sequences
      case (SURVEY_SECTION_LANDING, SURVEY_SECTION_QUESTIONS):
        // landing and question section always have at list 1 block @sinslav
        return Math.min(...activeSectionBlocks.map(property('page')));
      case SURVEY_SECTION_END:
        return endPageNumber;
      default:
        return 0;
    }
  },
);

export const getBlocksKeyedWithUuid = createSelector(getPaginatedSurveyData, (surveyData) =>
  [surveyData.landing, surveyData.questions, surveyData.end].reduce((clientBlocks, blocks) => {
    blocks.forEach((block) => {
      clientBlocks[block.clientUuid] = block;
    });
    return clientBlocks;
  }, {}),
);

export const getBlockByUuid = (state, clientUuid) =>
  get(getBlocksKeyedWithUuid(state), clientUuid, empty.object);

export const getQuestionIdByUuid = (state, clientUuid) => get(getBlockByUuid(state, clientUuid), 'id', null);

export const getBlockOptionIdByIndex = (state, clientUuid, optionIndex) => {
  const block = getBlockByUuid(state, clientUuid);
  return get(block, ['options', optionIndex, 'id']);
};

export const getCachedBlockData = (state, uuid) =>
  getCachedBlockDataMap(state)[uuid] || getBlockByUuid(state, uuid);

export const getBlockIndexInActiveSection = (state, clientUuid) =>
  state.orderMap[state.activeSectionName][clientUuid];

export const getActiveSectionBlockUuidByIndex = (state, index) => {
  return get(getActiveSectionBlocks(state), [index, 'clientUuid'], null);
};

export const getSectionPagesArray = createSelector(getSectionBlocks, (blockList) => {
  const pageMap = blockList.reduce((pages, currentBlock) => {
    if (!pages[currentBlock.page]) {
      pages[currentBlock.page] = [];
    }
    pages[currentBlock.page].push(currentBlock);
    return pages;
  }, {});

  let pages = Object.values(pageMap);
  if (!pages.length) {
    pages = [[]];
  }
  return pages;
});

const reduxActionsHandle = handleActions(
  {
    [LOCATION_CHANGE]: (state, { payload: { location } }) => {
      const isSurvey = location.pathname.startsWith(surveyEditorRoot);
      return update(state, {
        activeEntityType: {
          $set: isSurvey ? SURVEY_TYPE : TEMPLATE_TYPE,
        },
      });
    },
    [Constants.CACHE_BLOCK_DATA]: (state, action) => {
      const { uuid, data } = action.payload;
      const mergedBlockData = {
        ...getCachedBlockData(state, uuid),
        ...data,
      };
      return update(state, {
        cachedBlockDataMap: {
          [uuid]: {
            $set: mergedBlockData,
          },
        },
      });
    },

    [Constants.REMOVE_CACHED_BLOCK_DATA]: (state, action) => {
      return update(state, {
        cachedBlockDataMap: {
          $apply: (dataMap) => {
            const clonedDataMap = cloneDeep(dataMap);
            delete clonedDataMap[action.payload];
            return clonedDataMap;
          },
        },
      });
    },

    [Constants.SET_NETWORK_PENDING_ORDER_MAP]: (state, action) =>
      update(state, {
        pendingOrderMap: { $set: action.payload },
      }),

    [Constants.SORT_SECTION_BLOCK_LIST_BY_ORDER_MAP]: (state, action) => {
      const orderMap = action.payload;
      const activeSectionBlockList = state.surveyData[state.activeSectionName];
      const sortedSection = Object.keys(orderMap).map((uuid) => {
        return activeSectionBlockList.find((item) => item.clientUuid === uuid);
      });

      return update(state, {
        surveyData: {
          [state.activeSectionName]: {
            $set: sortedSection,
          },
        },
      });
    },

    [Constants.CLEAR_NETWORK_PENDING_ORDER_MAP]: (state) =>
      update(state, {
        pendingOrderMap: { $set: null },
      }),

    [Constants.SET_NETWORK_PENDING_ORDER_MAP_STATUS]: (state, action) =>
      update(state, {
        isMoveBlockNetworkPending: { $set: action.payload },
      }),

    [Constants.SET_ADD_BLOCK_NETWORK_PENDING_STATUS]: (state, action) =>
      update(state, {
        isAddBlockNetworkPending: { $set: action.payload },
      }),

    [Constants.CLEAR_PENDING_BLOCK_LIST]: (state, action) =>
      update(state, {
        pendingBlockList: {
          [action.payload]: {
            $set: [],
          },
        },
      }),

    [Constants.FILL_PENDING_BLOCK_LIST]: (state, action) => {
      const { block, sectionName } = action.payload;

      return update(state, {
        pendingBlockList: {
          [sectionName]: {
            $push: [block],
          },
        },
      });
    },

    [Constants.SET_EDITOR_ACTIVE_SURVEY]: (state, { payload: surveyData }) => {
      return update(state, {
        surveyData: {
          $set: surveyData,
        },
      });
    },

    [Constants.ADD_NEW_NOT_PERSISTED_BLOCK_UUID]: (state, action) => {
      return update(state, {
        newNotPersistedBlockUuidList: {
          $apply: (list) => uniq([...list, action.payload]),
        },
      });
    },

    [Constants.REMOVE_NEW_NOT_PERSISTED_BLOCK_UUID]: (state, action) => {
      return update(state, {
        newNotPersistedBlockUuidList: {
          $apply: (list) => without(list, ...action.payload),
        },
      });
    },

    [Constants.ADD_MODIFIED_NEW_NOT_PERSISTED_BLOCK_UUID]: (state, action) => {
      return update(state, {
        modifiedNewNotPersistedBlockUuidList: {
          $apply: (list) => uniq([...list, action.payload]),
        },
      });
    },

    [Constants.REMOVE_MODIFIED_NEW_NOT_PERSISTED_BLOCK_UUID]: (state, action) => {
      return update(state, {
        modifiedNewNotPersistedBlockUuidList: {
          $apply: (list) => without(list, action.payload),
        },
      });
    },

    [Constants.RESET_POST_SURVEY_LOGIC]: (state) =>
      update(state, {
        $merge: {
          postSurveyLogic: getReducerInitialState(postSurveyLogic),
        },
      }),

    [Constants.SET_LATEST_ADDED_BLOCK_CLIENT_UUID]: (state, action) =>
      update(state, {
        latestAddedBlockClientUuid: { $set: action.payload },
      }),

    [Constants.SET_LANDING_PAGE_ENABLED_STATE]: (state, action) =>
      update(state, {
        surveyData: {
          landingEnabled: { $set: action.payload },
        },
      }),

    [Constants.SET_THANK_YOU_PAGE_ENABLED_STATE]: (state, action) =>
      update(state, {
        surveyData: {
          endEnabled: { $set: action.payload },
        },
      }),

    [Constants.TOGGLE_SURVEY_FULL_WIDTH]: (state, { payload }) =>
      update(state, {
        surveyData: {
          isFullWidth: { $set: payload },
        },
      }),

    [Constants.SET_SHARED_HEADER]: (state, { payload: headerUuid }) =>
      update(state, {
        surveyData: {
          headerUuid: { $set: headerUuid },
        },
      }),

    [Constants.SET_ACTIVE_BLOCK_CLIENT_UUID]: (state, action) =>
      update(state, {
        activeBlock: {
          clientUuid: { $set: action.payload },
          isLogicPanelOpened: { $set: false },
        },
      }),

    [Constants.SET_IS_ACTIVE_BLOCK_LOGIC_PANEL_OPENED]: (state, action) =>
      update(state, {
        activeBlock: { isLogicPanelOpened: { $set: action.payload } },
      }),

    [Constants.SET_ACTIVE_BLOCK_TOUCHED_FLAG]: (state, { payload: uuid }) =>
      update(state, {
        touchedBlockUuidList: {
          $apply: (list) => uniq([...list, uuid]),
        },
      }),

    [Constants.SET_REDIRECT_URL]: (state, action) => {
      const redirectUrl = action.payload ? action.payload.toLowerCase().trim() : action.payload;

      return update(state, {
        surveyData: {
          redirectUrl: { $set: redirectUrl },
        },
      });
    },

    [Constants.MOVE_BLOCK]: (state, action) => {
      const { clientUuid, newIndex } = action.payload;
      const oldIndex = getBlockIndexInActiveSection(state, clientUuid);
      const block = getBlockByUuid(state, clientUuid);

      return update(state, {
        surveyData: {
          [state.activeSectionName]: {
            $splice: [[oldIndex, 1], [newIndex, 0, block]],
          },
        },
      });
    },

    [Constants.INSERT_BLOCK]: (state, action) => {
      const { block, index } = action.payload;

      return update(state, {
        surveyData: {
          [state.activeSectionName]: {
            $splice: [[index, 0, block]],
          },
        },
      });
    },

    [Constants.UPDATE_BLOCK]: (state, action) => {
      const { index, blockData } = action.payload;

      return update(state, {
        surveyData: {
          [state.activeSectionName]: {
            [index]: { $set: blockData },
          },
        },
      });
    },

    [Constants.SET_QUESTION_NUMBERS]: (state) => {
      return update(state, {
        surveyData: {
          questions: {
            $set: addQuestionNumbers(state.surveyData.questions),
          },
        },
      });
    },

    [Constants.CLEAR_SURVEY_SECTION]: (state, action) =>
      update(state, {
        surveyData: {
          [action.payload]: {
            $apply: (blockList) => (action.payload === SURVEY_SECTION_END ? [] : [{ ...last(blockList) }]),
          },
        },
      }),

    [Constants.REMOVE_UNSAVED_BLOCK_LIST]: (state, action) => {
      const uuidList = action.payload;
      const sanitizedSection = state.surveyData[state.activeSectionName].filter((item) =>
        uuidList.some((uuid) => uuid !== item.clientUuid),
      );

      return update(state, {
        surveyData: {
          [state.activeSectionName]: { $set: sanitizedSection },
        },
      });
    },

    [Constants.DELETE_BLOCK]: (state, action) => {
      const uuid = action.payload;

      const index = state.surveyData[state.activeSectionName].findIndex((block) => block.clientUuid === uuid);

      return update(state, {
        surveyData: {
          [state.activeSectionName]: {
            $splice: [[index, 1]],
          },
        },
        latestRemovedBlockClientUuid: { $set: uuid },
      });
    },

    [Constants.REPLACE_SURVEY_BLOCK]: (state, { payload: block }) => {
      const { clientUuid: searchUuid } = block;

      if (!searchUuid) {
        return Promise.reject();
      }

      const { index, sectionName } = ['landing', 'questions', 'end'].reduce(
        (r, sectionName) => {
          if (r.index < 0) {
            return {
              index: state.surveyData[sectionName].findIndex(({ clientUuid }) => clientUuid === searchUuid),
              sectionName,
            };
          }
          return r;
        },
        { index: -1 },
      );

      if (index < 0) {
        return Promise.reject();
      }

      return update(state, {
        surveyData: {
          [sectionName]: {
            [index]: { $set: block },
          },
        },
      });
    },

    [Constants.SET_EDITOR_ACTIVE_SECTION]: (state, action) =>
      update(state, {
        activeSectionName: { $set: action.payload },
      }),

    [Constants.RETURN_UNDELETE_BLOCK_LIST]: (state, action) => {
      const { sectionName, blockList } = action.payload;
      return update(state, {
        surveyData: {
          [sectionName]: {
            $set: blockList,
          },
        },
      });
    },
    [Constants.UPDATE_BLOCK_LIST_BULK]: (state, { payload: flattenedBlockList }) => {
      return update(state, {
        surveyData: {
          $apply: (surveyData) => ({
            ...surveyData,
            ...[landing, survey, ty].reduce(
              (accum, section) => ({
                ...accum,
                [section]: surveyData[section].map((currentBlock) =>
                  find(flattenedBlockList, (newBlock) => newBlock.clientUuid === currentBlock.clientUuid),
                ),
              }),
              {},
            ),
          }),
        },
      });
    },
    [Constants.START_UPDATE_BLOCK_LIST]: (state, action) => {
      const { blockList, sectionName } = action.payload;
      return update(state, {
        surveyData: {
          [sectionName]: {
            $apply: (questions) => {
              return questions.map(
                (question) =>
                  blockList[question.clientUuid]
                    ? { ...blockList[question.clientUuid], ...question }
                    : question,
              );
            },
          },
        },
      });
    },

    [Constants.UPDATE_ORDER_MAP]: (state) => {
      const updatedOrderMap = Object.keys(state.orderMap).reduce(
        (map, sectionName) => ({
          ...map,
          [sectionName]: state.surveyData[sectionName].reduce(
            (blockList, block, index) => ({
              ...blockList,
              [block.clientUuid]: index,
            }),
            {},
          ),
        }),
        {},
      );

      return update(state, {
        orderMap: { $set: updatedOrderMap },
      });
    },

    [Constants.UPDATE_EDIT_SURVEY_STATUS]: (state, { payload }) => {
      return update(state, {
        surveyData: {
          $merge: payload,
        },
      });
    },

    [Constants.SET_EDITOR_LOADING_STATE]: (state, action) =>
      update(state, {
        isLoading: { $set: action.payload },
      }),

    // TODO: Refactor after extracting Settings into separate reducer @etiurin
    [Constants.CHANGE_SURVEY_SETTING]: (state, { payload: settings }) => {
      if (settings.reportSettings) {
        settings.reportSettings = update(get(state, 'surveyData.settings.reportSettings', {}), {
          $merge: settings.reportSettings,
        });
      }

      return update(state, {
        surveyData: {
          settings: {
            $merge: settings,
          },
        },
      });
    },

    [Constants.SET_EDITOR_SURVEY_NAME]: (state, action) =>
      update(state, {
        surveyData: {
          name: {
            $set: action.payload,
          },
        },
      }),

    [Constants.SET_SHARE_URL_SLUG]: (state, { payload: slug }) =>
      update(state, {
        slug: { $set: slug },
      }),

    [Constants.SET_OVERWRITE_CUSTOM_STYLES]: (state, { payload }) =>
      update(state, {
        isSetOverwriteCustomStylesEnabled: {
          $set: payload,
        },
      }),

    [Constants.SET_IS_SURVEY_NAME_FOCUSED]: (state, { payload }) =>
      update(state, {
        isSurveyNameFocused: {
          $set: payload,
        },
      }),

    [Constants.SET_SURVEY_STATUS_PENDING_STATE]: (state, { payload }) =>
      update(state, {
        surveyData: {
          isStatusPending: {
            $set: payload,
          },
        },
      }),

    [Constants.SET_SURVEY_HEADER_END_ENABLED]: (state, { payload }) =>
      update(state, {
        surveyData: {
          headerEndEnabled: {
            $set: payload,
          },
        },
      }),

    [Constants.SET_SURVEY_HEADER_LANDING_ENABLED]: (state, { payload }) =>
      update(state, {
        surveyData: {
          headerLandingEnabled: {
            $set: payload,
          },
        },
      }),

    [Constants.SET_SURVEY_HEADER_QUESTIONS_ENABLED]: (state, { payload }) =>
      update(state, {
        surveyData: {
          headerQuestionsEnabled: {
            $set: payload,
          },
        },
      }),
  },
  initialState,
);

export default (state, action) => reduxActionsHandle(switchHandle(state, action), action);

export const getThumbnailPageNumber = (state) =>
  isLandingEnabled(state) ? null : isQuestionsSectionOnePage(state) ? null : 1;

export const isFullWidthSurvey = (state) => getRawSurveyData(state).isFullWidth;

export const getRedirectUrl = (state) => getRawSurveyData(state).redirectUrl;

export const getSurveyName = (state) => getRawSurveyData(state).name;

export const isSurveyNameFocused = (state) => state.isSurveyNameFocused;

export const getSurveyStatus = (state) => getRawSurveyData(state).status;

export const isSurveyClosed = (state) => getSurveyStatus(state) === SURVEY_STATUS_CLOSED;

export const getEntityId = (state) => getRawSurveyData(state).id;

export const getHeaderEndEnabled = (state) => getRawSurveyData(state).headerEndEnabled;

export const getHeaderLandingEnabled = (state) => getRawSurveyData(state).headerLandingEnabled;

export const getHeaderQuestionsEnabled = (state) => getRawSurveyData(state).headerQuestionsEnabled;

export const getHeaderEnabled = (state, sectionName) => {
  const { landing: sectionLanding, survey: sectionQuestions, ty: sectionEnd } = mapEditorSectionToUrl;

  return ({
    [sectionEnd]: getHeaderEndEnabled,
    [sectionLanding]: getHeaderLandingEnabled,
    [sectionQuestions]: getHeaderQuestionsEnabled,
  }[sectionName] || noop)(state);
};

export const isEditorLoading = (state) => state.isLoading;

export const isSurveyEntityType = (state) => getActiveEntityType(state) === SURVEY_TYPE;

export const isSurveyStatusDraft = (state) => getSurveyStatus(state) === SURVEY_STATUS_DRAFT;

export const isSurveyStatusPaused = (state) => getSurveyStatus(state) === SURVEY_STATUS_PAUSED;

const isSurveyPausedOrDraft = createSelector(getSurveyStatus, (status) =>
  [SURVEY_STATUS_DRAFT, SURVEY_STATUS_PAUSED].includes(status),
);

export const shouldRestrictAddBlockToSurvey = (state) =>
  isEditorLoading(state) ||
  (isSurveyEntityType(state) && !isSurveyStatusDraft(state) && !isSurveyStatusPaused(state));

export const shouldRestrictChangeGlobalStyling = createSelector(
  isSurveyEntityType,
  isEditorLoading,
  isSurveyPausedOrDraft,
  (isSurvey, isLoading, isPausedOrDraft) => isLoading || (isSurvey && !isPausedOrDraft),
);

export const getEntityStatus = (state) => getRawSurveyData(state).status;

export const getSectionUrl = (state, sectionName) =>
  getUrl.section({
    root: getCurrentRouterRootPath(state),
    entityId: getEntityId(state),
    sectionName,
  });

export const getActiveSectionUrl = (state) => getSectionUrl(state, getActiveSection(state));

export const getSurveySettings = (state) => getRawSurveyData(state).settings || {};

export const getSurveySetting = (state, name) => get(getSurveySettings(state), name);

export const getSurveyUpdateFrequency = (state) => getSurveySetting(state, 'updateFrequency');

export const getQuestionNumberingEnabled = (state) => getSurveySetting(state, 'questionNumberingEnabled');

export const isUpdateFrequencyEnabled = (state) => getSurveyUpdateFrequency(state) !== REPORT_FREQUENCY_NONE;

export const getOrderMap = (state) => state.orderMap;

export const getOrderMapBySection = (sectionName) =>
  createSelector(getOrderMap, (orderMap) => orderMap[sectionName]);

export const getActiveSectionOrderMap = createSelector(
  getOrderMap,
  getActiveSection,
  (orderMap, activeSection) => orderMap[activeSection],
);

export const isAddBlockNetworkPending = (state) => state.isAddBlockNetworkPending;

export const getNetworkPendingBlockList = (state) => state.pendingBlockList;

export const hasActiveSectionBlocks = createSelector(getActiveSectionBlocks, (blocks) => !isEmpty(blocks));

export const getActiveSectionPages = (state) => getSectionPagesArray(state, getActiveSection(state));

export const getLandingSectionBlocks = (state) => getSectionBlocks(state, 'landing');

export const getQuestionsSectionBlocks = (state) => getSectionBlocks(state, 'questions');

export const getThankYouSectionBlocks = (state) => getSectionBlocks(state, 'end');

export const isBlockTypeInSection = (state, sectionName, blockType) =>
  getSectionBlockTypes(state, sectionName).some((item) => item === blockType);

export const getLandingSectionLength = (state) => get(getRawSurveyData(state), 'landing.length', 0);

export const isLandingSectionSuccessful = (state) => {
  if (isSectionActive(state, landing)) {
    return false;
  }

  if (!isLandingEnabled(state)) {
    return true;
  }

  return getLandingSectionLength(state) >= properSectionWithNextButtonLength;
};

export const getQuestionsSectionLength = (state) => get(getRawSurveyData(state), 'questions.length', 0);

export const isQuestionsSectionSuccessful = (state) => {
  if (isSectionActive(state, survey)) {
    return false;
  }

  return getQuestionsSectionLength(state) >= properSectionWithNextButtonLength;
};

export const getThankYouSectionLength = (state) => get(getRawSurveyData(state), 'end.length', 0);

export const isThankYouSectionSuccessful = (state) => {
  if (isSectionActive(state)) {
    return false;
  }

  if (isThankYouEnabled(state)) {
    return getThankYouSectionLength(state) > 0;
  }

  return isEmpty(validateRedirectUrl(false, getRedirectUrl(state)));
};

export const getActiveSectionPlaceholder = (state) => {
  const placeholders = {
    [landing]: 'Add an element from the right sidebar.',
    [survey]: 'Add a question from the right sidebar.',
  };

  return get(placeholders, getActiveSection(state), null);
};

export const isBuilderSectionActive = (state) =>
  surveyBuilderSections.some((section) => isSectionActive(state, section));

export const hasRequiredEmailQuestion = createSelector(getLandingSectionBlocks, (blocks) => {
  const emailQuestion = blocks.find((block) => block.type === questionTypes.respondentMail);
  return emailQuestion && emailQuestion.required;
});

export const getActiveBlock = (state) => state.activeBlock;

export const getActiveBlockClientUuid = (state) => getActiveBlock(state).clientUuid;

export const isActiveBlockLogicPanelOpened = (state) => getActiveBlock(state).isLogicPanelOpened;

export const isSetOverwriteCustomStylesEnabled = (state) => state.isSetOverwriteCustomStylesEnabled;

export const getLatestAddedBlockClientUuid = (state) => state.latestAddedBlockClientUuid;

export const getLatestRemovedBlockClientUuid = (state) => state.latestRemovedBlockClientUuid;

export const getBlockType = (state, clientUuid) => get(getBlockByUuid(state, clientUuid), 'type');

export const getBlockConfig = (state, clientUuid) => blockConfig[getBlockType(state, clientUuid)] || {};

export const isUniqueBlockType = (state, sectionName, blockType) =>
  blockConfig[blockType].settings.isUniqueField(sectionName);

export const isBlockActive = (state, clientUuid) => getActiveBlockClientUuid(state) === clientUuid;

export const isBlockTouched = (state, clientUuid) => state.touchedBlockUuidList.includes(clientUuid);

export const isBlockTextContentPresent = (state, clientUuid) => {
  const { plainText, content } = getBlockByUuid(state, clientUuid);
  if (content && !isEmpty(content.entityMap)) {
    return true;
  }

  // need to trim to handle case when there are only spaces @lukavyi
  return !!(plainText && plainText.trim());
};

export const getQuestionById = (state, id) => find(getQuestions(state), { id }) || {};

export const getQuestionType = (state, id) => getQuestionById(state, id).type;

export const getLogicSuggestions = (state, currentQuestionUuid) =>
  questionLogicUtils.getLogicSuggestions(getQuestionsSectionBlocks(state), currentQuestionUuid);

export const getQuestionLogic = (state, clientUuid) =>
  questionLogicUtils.getQuestionLogic(getBlockByUuid(state, clientUuid));

export const getQuestionLogicList = (state, clientUuid) =>
  questionLogicUtils.getQuestionLogicList(getBlockByUuid(state, clientUuid));

export const getQuestionRegardlessLogicUuid = (state, clientUuid) =>
  questionLogicUtils.getQuestionRegardlessLogicUuid(getBlockByUuid(state, clientUuid));

export const isQuestionLogicValid = (state, clientUuid) =>
  questionLogicUtils.isQuestionLogicValid(getBlockByUuid(state, clientUuid));

export const isQuestionLogicActionShow = (state, clientUuid) =>
  questionLogicUtils.isQuestionLogicShow(getBlockByUuid(state, clientUuid));

export const isQuestionLogicActionSkip = (state, clientUuid) =>
  questionLogicUtils.isQuestionLogicSkip(getBlockByUuid(state, clientUuid));

export const isQuestionUsedInOtherQuestionLogic = (state, clientUuid) =>
  questionLogicUtils.isQuestionUsedInList(clientUuid, getQuestionsSectionBlocks(state));

export const isQuestionTextInvalid = (state, clientUuid) => {
  const isActive = isBlockActive(state, clientUuid);
  const isTextPresent = isBlockTextContentPresent(state, clientUuid);
  const isTouched = isBlockTouched(state, clientUuid);
  const { settings } = getBlockConfig(state, clientUuid);
  const shouldHaveText = settings && settings.isTextRequired();

  return shouldHaveText && !isTextPresent && !isActive && isTouched;
};

export const isUniqueBlock = (state, clientUuid) => {
  const activeSection = getActiveSection(state);
  const currentBlockConfig = getBlockConfig(state, clientUuid);

  return currentBlockConfig.settings && currentBlockConfig.settings.isUniqueField(activeSection);
};

export const isSavedBlock = (state, clientUuid) => !!getBlockByUuid(state, clientUuid).id;

export const getPrevBlockUuidInActiveSection = (state, clientUuid) => {
  const prevBlockIndex = getBlockIndexInActiveSection(state, clientUuid) - 1;
  if (prevBlockIndex < 0) {
    return null;
  }

  return getActiveSectionBlockUuidByIndex(state, prevBlockIndex);
};

export const hasPrevBlockShowLogic = (state, clientUuid) => {
  const prevBlockUuid = getPrevBlockUuidInActiveSection(state, clientUuid);
  return isQuestionLogicActionShow(state, prevBlockUuid);
};

export const getNextBlockUuidInActiveSection = (state, clientUuid) => {
  const nextBlockIndex = getBlockIndexInActiveSection(state, clientUuid) + 1;
  if (nextBlockIndex > getActiveSectionBlocks(state).length - 1) {
    return null;
  }

  return getActiveSectionBlockUuidByIndex(state, nextBlockIndex);
};

export const getLastQuestionUuidOnActiveSection = createSelector(getActiveSectionBlocks, (blocks) => {
  const lastBlockIndex = findLastIndex((block) => block.type !== questionTypes.submitButton);
  return get(blocks, `${lastBlockIndex}.clientUuid`, null);
});

export const doesLastQuestionHaveValidLogic = (state) => {
  const lastBlockUuid = getLastQuestionUuidOnActiveSection(state);
  return isQuestionLogicValid(state, lastBlockUuid);
};

export const getActiveSectionBlockMaxAvailableIndex = (state) => {
  const activeSectionName = getActiveSection(state);
  const activeSectionLength = getActiveSectionBlocks(state).length;

  return activeSectionName === ty ? activeSectionLength : activeSectionLength - 1;
};

export const isBlockChangingToolsEnabled = (state, clientUuid) => {
  const activeSection = getActiveSection(state);
  const currentBlockType = getBlockType(state, clientUuid);
  const isNextButtonOnLandingPage =
    currentBlockType === questionTypes.nextButton && activeSection === landing;
  const prevBlockUuid = getPrevBlockUuidInActiveSection(state, clientUuid);
  const isNextOrSubmitButton =
    currentBlockType === questionTypes.nextButton || currentBlockType === questionTypes.submitButton;
  const isNextOrSubmitButtonAfterLogicQuestion =
    isNextOrSubmitButton && isQuestionLogicValid(state, prevBlockUuid);

  if (isNextButtonOnLandingPage || isNextOrSubmitButtonAfterLogicQuestion) {
    return false;
  }

  return currentBlockType !== questionTypes.submitButton;
};

export const getUnusedRespondentInfoMorphOptionList = createSelector(getPaginatedSurveyData, (surveyData) => {
  const landingBlockList = get(surveyData, landing, []);
  return respondentBlockMorphList.filter((item) => {
    const isUnique = blockConfig[item.value].settings.isUniqueField();
    const isInLanding = landingBlockList.some((block) => block.type === item.value);
    return !(isUnique && isInLanding);
  });
});

// @todo refactor @lukavyi @sinslav
const getQuestionOptions = (question) => {
  const { options } = question;
  const count = get(question, 'itemList.length', 0);
  return (
    options ||
    (count && createEmptyOptionCollection(count)) || // @todo use labels here as well @feshchenko
    []
  );
};

export const getQuestionOptionsById = (state, questionId) => {
  const question = getQuestionById(state, questionId);
  return getQuestionOptions(question);
};

export const getQuestionOptionsByUuid = (state, questionUuid) => {
  const block = getBlockByUuid(state, questionUuid);
  return getQuestionOptions(block);
};

export const getNetworkPendingOrderMap = (state) => state.pendingOrderMap;

export const isMoveBlockNetworkPending = (state) => state.isMoveBlockNetworkPending;

export const canMoveBlock = (state, clientUuid, newIndex) => {
  if (!isBlockChangingToolsEnabled(state, clientUuid) || isQuestionLogicValid(state, clientUuid)) {
    return false;
  }

  const activeSectionBlocks = getActiveSectionBlocks(state);

  if (newIndex < 0 || newIndex > activeSectionBlocks.length - 1) {
    return false;
  }

  const nextBlockUuid = getNextBlockUuidInActiveSection(state, clientUuid);
  if (isQuestionLogicValid(state, nextBlockUuid)) {
    const logicNextBlockUuid = getNextBlockUuidInActiveSection(state, nextBlockUuid);
    const logicNextBlockType = getBlockType(state, logicNextBlockUuid);
    const isSubmitType = logicNextBlockType === questionTypes.submitButton;
    const nextBlockIndex = getBlockIndexInActiveSection(state, nextBlockUuid);

    if (isSubmitType && newIndex >= nextBlockIndex) {
      return false;
    }
  }

  const block = getBlockByUuid(state, clientUuid);

  const isNextButton = block.type === questionTypes.nextButton;
  const isSubmitButton = block.type === questionTypes.submitButton;

  const nextBtnIndex = findLastIndex(activeSectionBlocks, {
    type: questionTypes.nextButton,
  });
  const submitBtnIndex = findLastIndex(activeSectionBlocks, {
    type: questionTypes.submitButton,
  });

  const landingCondition = nextBtnIndex !== -1 && newIndex < nextBtnIndex;
  const surveyCondition = submitBtnIndex !== -1 && newIndex < submitBtnIndex;

  const activeSectionName = getActiveSection(state);

  switch (activeSectionName) {
    case SURVEY_SECTION_LANDING:
      return landingCondition && !isNextButton;
    case SURVEY_SECTION_QUESTIONS:
      return surveyCondition && !isSubmitButton;
    case SURVEY_SECTION_END:
      return true;
    default:
      return false;
  }
};

export const canMoveBlockUp = (state, clientUuid) => {
  const newIndex = getBlockIndexInActiveSection(state, clientUuid) - 1;

  return canMoveBlock(state, clientUuid, newIndex);
};

export const canMoveBlockDown = (state, clientUuid) => {
  const newIndex = getBlockIndexInActiveSection(state, clientUuid) + 1;

  return canMoveBlock(state, clientUuid, newIndex);
};

export const getSurveySlug = (state) => state.slug;

export const getPostLogic = (state) => state.postSurveyLogic || {};

export const getPostLogicSelectedQuestionIds = (state) =>
  fromPostSurvey.getSelectedQuestionIds(getPostLogic(state));

export const getPostLogicItemIds = (state) => fromPostSurvey.getItemIds(getPostLogic(state));

export const getPostLogicRules = (state, itemId) => fromPostSurvey.getRules(getPostLogic(state), itemId);

export const getPostLogicPersistedRulesCount = (state, itemId) =>
  fromPostSurvey.getPersistedRulesCount(getPostLogic(state), itemId);

export const getPostLogicRuleClientIds = (state, itemId) =>
  fromPostSurvey.getRuleClientIds(getPostLogic(state), itemId);

export const getPostLogicQuestionId = (state, itemId) =>
  fromPostSurvey.getQuestionId(getPostLogic(state), itemId);

export const isPostLogicItemExpanded = (state, itemId) =>
  fromPostSurvey.isItemExpanded(getPostLogic(state), itemId);

export const getPostLogicRule = (state, clientId) => fromPostSurvey.getRule(getPostLogic(state), clientId);

export const getPostLogicRulePersistenceId = (state, clientId) =>
  fromPostSurvey.getRulePersistenceId(getPostLogic(state), clientId);

export const isPostLogicReceived = (state) => fromPostSurvey.isReceived(getPostLogic(state));

export const getPostLogicConfiguredQuestionsCount = (state) => {
  return fromPostSurvey.isReceived(getPostLogic(state))
    ? fromPostSurvey.getPersistedQuestionsCount(getPostLogic(state))
    : getSurveySetting(state, 'postLogicQuestionsCount');
};

const getPostLogicSupportedQuestions = createSelector(getQuestions, (questions) =>
  questions
    .filter((block) => !isPageBreak(block))
    .map((props, index) => ({
      displayOrder: index,
      ...props,
    }))
    .filter((question) => isPostLogicAvailableBlockType(question.type)),
);

export const getPostLogicAvailableQuestions = createSelector(
  getPostLogicSelectedQuestionIds,
  getPostLogicQuestionId,
  getPostLogicSupportedQuestions,
  (selectedQuestionIds, selectedQuestionId, questions) =>
    questions
      .map(({ id, plainText, type, displayOrder }) => ({
        id,
        plainText: questionLogicUtils.generateLabelWithQuestionType(plainText, type, displayOrder),
        primaryLabel: plainText,
        secondaryLabel: questionLogicUtils.generateSecondaryLabel(displayOrder, type),
      }))
      .filter(({ id }) => !selectedQuestionIds.includes(id) || id === selectedQuestionId),
);

export const canOpenPostSurveyLogic = (state) =>
  isLandingEnabled(state) && hasRequiredEmailQuestion(state) && getPostLogicSupportedQuestions(state).length;

export const getPreviewBlocks = (state) => getSectionBlocks(state, getActiveSection(state));

export const hasSectionHeader = (state, sectionName) =>
  fromHeader.hasSectionHeader(state.sectionHeaders, sectionName);

export const getHeaderData = (state) => fromHeader.getHeaderData(state.sectionHeaders);

export const hasPersistedSectionHeader = (state, sectionName) =>
  fromHeader.hasPersistedSectionHeader(state.sectionHeaders, sectionName);

export const getSectionHeader = (state, sectionName) =>
  fromHeader.getSectionHeader(state.sectionHeaders, sectionName);

export const getSectionHeaderBackgroundImageUrl = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundImageUrl(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageUrl = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageUrl(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageSizePreset = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageSizePreset(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageSize = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageSize(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageAlign = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageAlign(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoWithTextAlign = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoWithTextAlign(state.sectionHeaders, sectionName);

export const getSectionHeaderBackgroundImageSize = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundImageSize(state.sectionHeaders, sectionName);

export const getSectionHeaderHeight = (state, sectionName) =>
  fromHeader.getSectionHeaderHeight(state.sectionHeaders, sectionName);

export const getSectionHeaderBackgroundColor = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundColor(state.sectionHeaders, sectionName);

export const isSectionHeaderFullWidth = (state, sectionName) =>
  fromHeader.isSectionHeaderFullWidth(state.sectionHeaders, sectionName) || isFullWidthSurvey(state);

export const getSectionHeaderBackgroundCoords = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundCoords(state.sectionHeaders, sectionName);

export const isSectionHeaderPlaceholder = (state, sectionName) =>
  fromHeader.isSectionHeaderPlaceholder(state.sectionHeaders, sectionName);

export const isSectionHeaderContentEnabled = (state, sectionName) =>
  fromHeader.isSectionHeaderContentEnabled(state.sectionHeaders, sectionName);

export const getSectionHeaderLayout = (state, sectionName) =>
  fromHeader.getSectionHeaderLayout(state.sectionHeaders, sectionName);

export const getSectionHeaderTextContent = (state, sectionName) =>
  fromHeader.getSectionHeaderTextContent(state.sectionHeaders, sectionName);

export const getSectionHeaderClientUuid = (state, sectionName) =>
  fromHeader.getSectionHeaderClientUuid(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageLinkUrl = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageLinkUrl(state.sectionHeaders, sectionName);

export const getHeaderPageBoxType = (state, pageIndex, sectionName) => {
  if (pageIndex !== 0) {
    return null;
  }

  return fromHeader.getHeaderPageBoxType(state.sectionHeaders, sectionName, isFullWidthSurvey(state));
};

export const getEditorSharedHeader = (state) =>
  fromHeader.getHeaderByUuid(state.sectionHeaders, getRawSurveyData(state).headerUuid);

export const getSharedHeaderSection = (state) => get(getEditorSharedHeader(state), 'sectionAssignee');

export const hasEditorSharedHeader = (state) => !!getEditorSharedHeader(state);

export const isActiveSectionHeaderExist = (state) => hasSectionHeader(state, getActiveSection(state));

export const getActiveSectionHeader = (state) => getSectionHeader(state, getActiveSection(state));

export const getActiveSectionHeaderBackgroundImageUrl = (state) =>
  getSectionHeaderBackgroundImageUrl(state, getActiveSection(state));

export const getActiveSectionHeaderLogoImageUrl = (state) =>
  getSectionHeaderLogoImageUrl(state, getActiveSection(state));

export const getActiveSectionHeaderLogoImageSizePreset = (state) =>
  getSectionHeaderLogoImageSizePreset(state, getActiveSection(state));

export const getActiveSectionHeaderLogoImageSize = (state) =>
  getSectionHeaderLogoImageSize(state, getActiveSection(state));

export const getActiveSectionHeaderLogoImageAlign = (state) =>
  getSectionHeaderLogoImageAlign(state, getActiveSection(state));

export const getActiveSectionHeaderLogoWithTextAlign = (state) =>
  getSectionHeaderLogoWithTextAlign(state, getActiveSection(state));

export const getActiveSectionHeaderBackgroundImageSize = (state) =>
  getSectionHeaderBackgroundImageSize(state, getActiveSection(state));

export const getActiveSectionHeaderHeight = (state) => getSectionHeaderHeight(state, getActiveSection(state));

export const getActiveSectionHeaderBackgroundColor = (state) =>
  getSectionHeaderBackgroundColor(state, getActiveSection(state));

export const isActiveSectionHeaderFullWidth = (state) =>
  isSectionHeaderFullWidth(state, getActiveSection(state));

export const isSharedHeaderFullWidth = (state) =>
  isSectionHeaderFullWidth(state, getSharedHeaderSection(state));

export const getActiveSectionHeaderBackgroundCoords = (state) =>
  getSectionHeaderBackgroundCoords(state, getActiveSection(state));

export const isActiveSectionHeaderPlaceholder = (state) =>
  isSectionHeaderPlaceholder(state, getActiveSection(state));

export const getActiveSectionHeaderPageBoxType = (state, pageIndex) =>
  getHeaderPageBoxType(state, pageIndex, getActiveSection(state));

export const doesActiveSectionHasHeader = (state) => !!getActiveSectionHeader(state);

export const isActiveSectionHeaderContentEnabled = (state) =>
  isSectionHeaderContentEnabled(state, getActiveSection(state));

export const getActiveSectionHeaderLogoImageLinkUrl = (state) =>
  getSectionHeaderLogoImageLinkUrl(state, getActiveSection(state));

export const getActiveSectionHeaderLayout = (state) => getSectionHeaderLayout(state, getActiveSection(state));

export const getActiveSectionHeaderTextContent = (state) =>
  getSectionHeaderTextContent(state, getActiveSection(state));

export const getActiveSectionHeaderClientUuid = (state) =>
  getSectionHeaderClientUuid(state, getActiveSection(state));

export const getFormOptionList = ({ state, form, initialValues, block }) =>
  get(getFormValues(form)(state) || initialValues, 'options', block.options || empty.array);

export const getMatrixOptionCount = ({ state, form, initialValues, block = {} }) =>
  get(getFormOptionList({ state, form, initialValues, block }), 'length', 0);

export const getMatrixLabelList = ({ state, form, initialValues, block }) =>
  get(getFormValues(form)(state) || initialValues, itemList, block[itemList] || empty.array);

export const getMatrixInvalidRowFormList = (state, formList) =>
  formList.reduce((invalidFormList, form) => {
    if (hasSubmitFailed(form)(state) && isInvalid(form)(state)) {
      invalidFormList.push(form);
    }
    return invalidFormList;
  }, []);

export const isSomeMatrixRowFormInvalid = ({ state, formList = [] }) =>
  !!getMatrixInvalidRowFormList(state, formList).length;

export const getOrderedSectionBlockList = (sectionName) =>
  createSelector(
    getRawSectionBlockList(sectionName),
    getOrderMapBySection(sectionName),
    (sectionBlockList, sectionOrderMap) =>
      sectionBlockList.map((block) => ({
        ...block,
        order: sectionOrderMap[block.clientUuid],
      })),
  );

export const getLandingOrderedBlockList = getOrderedSectionBlockList(SURVEY_SECTION_LANDING);

export const getQuestionsOrderedBlockList = getOrderedSectionBlockList(SURVEY_SECTION_QUESTIONS);

export const getEndOrderedBlockList = getOrderedSectionBlockList(SURVEY_SECTION_END);

export const getOrderedBlockList = createSelector(
  getLandingOrderedBlockList,
  getQuestionsOrderedBlockList,
  getEndOrderedBlockList,
  (landing, questions, end) => [...landing, ...questions, ...end],
);

export const isStatusPending = (state) => state.surveyData.isStatusPending;

export const isActiveSectionRequiredButtonDefined = (state) => stickyButtonsConfig[getActiveSection(state)];

export const isActiveSectionRequiredBlockNeeded = (state) =>
  isEmpty(getActiveSectionBlocks(state)) && isActiveSectionRequiredButtonDefined(state);

export const getFirstPageBlockList = createSelector(
  isLandingEnabled,
  getLandingSectionBlocks,
  getQuestionsSectionBlocks,
  (isLanding, blocksLanding, blocksQuestions) =>
    isLanding ? blocksLanding : blocksQuestions.filter((block) => get(block, 'page') === 1),
);

export const isBackButtonEnabled = (state) => getSurveySettings(state).isBackButtonEnabled;
